import Axios from "axios";

import { compressImage, FileType } from "@utils";

export interface UploadResponse {
  url: string;
  fields: {
    key: string;
    policy: string;
    "x-amz-algorithm": string;
    "x-amx-credential": string;
    "x-amx-date": string;
    "x-amx-signature": string;
  };
}

export const AWS_REGION = "eu-west-1";

export async function awsUpload(dataToUpload: UploadResponse, file: File) {
  const data = new FormData();
  Object.entries(dataToUpload.fields).forEach(([key, value]) => {
    data.append(key, value);
  });
  data.append("file", file);

  await fetch(dataToUpload.url, {
    method: "POST",
    body: data,
  });
}

export async function upload(fileType: FileType, file_name?: string) {
  const url = `/api/files/upload/${fileType}/`;
  const response = await Axios.get<UploadResponse>(url, {
    params: { file_name: file_name || undefined },
  });
  return response.data;
}

const COMPRESSIBLE_FILE_TYPES: ReadonlySet<FileType> = new Set([
  FileType.AuctionImage,
  FileType.Media,
]);

export interface UploadResult {
  url: string;
  key: string;
  fullUrl: string;
}

export async function uploadFile(
  fileType: FileType,
  file: File,
  fileName?: string,
): Promise<UploadResult> {
  const processedFile = COMPRESSIBLE_FILE_TYPES.has(fileType)
    ? await compressImage(file, fileName)
    : file;

  const dataToUpload = await upload(fileType, processedFile.name);
  await awsUpload(dataToUpload, processedFile);

  return {
    url: dataToUpload.url,
    key: dataToUpload.fields.key,
    fullUrl: `${dataToUpload.url}/${dataToUpload.fields.key}`,
  };
}

export async function uploadFiles(fileType: FileType, files: File[]): Promise<UploadResult[]> {
  return Promise.all(files.map((file) => uploadFile(fileType, file)));
}

export async function download(fileType: FileType, name: string) {
  const response = await Axios.get<{
    presigned_url: string;
  }>(`/api/files/download/${fileType}/${name}/`);
  return response.data;
}

export async function awsDelete(fileType: FileType, region: string, keys: string[]) {
  const response = await Axios.post(
    `/api/files/delete/${fileType}/`,
    { keys },
    { params: { region } },
  );
  return response.data;
}
